import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { TbMessageChatbot } from "react-icons/tb";
import { FiX, FiSend } from "react-icons/fi";
import { IoMdSend } from "react-icons/io";
import { PiMagicWand } from "react-icons/pi";
import { PiPaintBrushHousehold } from "react-icons/pi";
import { IoReorderFourOutline } from "react-icons/io5";
import { BsArrowsCollapseVertical } from "react-icons/bs";
import { IoSearchOutline } from "react-icons/io5";
import { MdOutlineTranslate } from "react-icons/md";

/* ================= Config (swap out easily) ================= */
const API_URL =
  "https://chatbot.studentdiwan.com:5678/webhook/b9ad8408-d6dc-4a75-af73-30ff92016361/chat";
const SESSION_ID = "aee98f067648495ea3f27b8534f8f8bd";



/* ================= Small UI primitives ================= */
const Chip = memo(function Chip({ children, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 rounded-lg bg-[#f6ebfc] px-3 py-1.5 text-sm hover:shadow-sm transition"
    >
      {children}
    </button>
  );
});

const SectionCard = memo(function SectionCard({ title, prompts, onPromptClick }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      <div className="mt-3 space-y-2">
        {prompts.map((p, i) => (
          <button
            key={i}
            onClick={() => onPromptClick(p)}
            className="w-full text-left rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:bg-gray-100"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
});

const Accordion = memo(function Accordion({ items, onPromptClick }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-2">
      {items.map((it, idx) => (
        <div key={idx} className="rounded-xl border border-gray-200 bg-white">
          <button
            className="flex w-full items-center justify-between px-4 py-3 text-left font-medium"
            onClick={() => setOpen(open === idx ? -1 : idx)}
          >
            <span>{it.title}</span>
            <span className="text-gray-500">{open === idx ? "▾" : "▸"}</span>
          </button>
          {open === idx && (
            <div className="border-t border-gray-100 p-3 space-y-2">
              {it.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => onPromptClick(opt)}
                  className="w-full text-left rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:bg-gray-100"
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
});

/* ================= Main Component ================= */
export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const listRef = useRef(null);

  const toggleChat = () => {
    setIsOpen((v) => !v);
    if (isOpen) {
      setMessages([]);
      setChatInput("");
    }
  };

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, isLoading]);

  const sendMessage = useCallback(async () => {
    const text = chatInput.trim();
    if (!text || isLoading) return;
    const userMessage = { from: "user", text };
    setMessages((prev) => [...prev, userMessage]);
    setChatInput("");
    setIsLoading(true);

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: SESSION_ID,
          action: "sendMessage",
          chatInput: text,
        }),
      });

      const data = await res.json();
      const botText =
        (data && (data.output || data.message || data.text)) ||
        "⚠️ No response.";
      setMessages((prev) => [...prev, { from: "bot", text: botText }]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { from: "bot", text: "⚠️ Error contacting bot." },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [chatInput, isLoading]);

  const onPromptClick = useCallback((p) => {
    setChatInput(p);
  }, []);

  /* ====== Content for UI blocks (matches screenshot) ====== */
  const outlinePrompt = "Create a structured course outline with clear learning objectives and modules.";
  const courseSections = [
    { title: "Course Outline", prompts: [outlinePrompt, outlinePrompt] },
    { title: "Course Outline", prompts: [outlinePrompt, outlinePrompt] },
    { title: "Course Outline", prompts: [outlinePrompt, outlinePrompt] },
  ];

  const accordionItems = [
    {
      title: "Create a course outline for a [Subject] course",
      options: [
        "Outline a 6-week beginner course on [Subject] with weekly objectives and assessments.",
      ],
    },
    {
      title: "Develop a lesson plan for a [Topic] lesson",
      options: [
        "Create a 45-minute lesson plan for [Topic] with activities and exit ticket.",
      ],
    },
    {
      title: "Design an interactive quiz on [Concept]",
      options: ["Generate a 10-question quiz (MCQs + short answers) on [Concept]."],
    },
  ];

  //  {courseSections.map((sec, idx) => (
  //           <SectionCard
  //             key={idx}
  //             title={sec.title}
  //             prompts={sec.prompts}
  //             onPromptClick={onPromptClick}
  //           />
  //         ))}

  //         <div className="rounded-xl border border-gray-200 bg-white p-4">
  //           <h3 className="text-sm font-semibold text-gray-700">More Prompts</h3>
  //           <div className="mt-3">
  //             <Accordion items={accordionItems} onPromptClick={onPromptClick} />
  //           </div>
  //         </div>


  return (
    <div className="fixed bottom-24 end-8 z-[999999] font-sans">

      {!isOpen && (
        <button
          onClick={toggleChat}
          className="bg-purple-600 text-white p-3 rounded-full shadow-lg hover:bg-purple-700 transition-all duration-300 animate-float"
          aria-label="Open chatbot"
        >
          <TbMessageChatbot size={30} />
        </button>
      )}

      {isOpen && (
        <div className="w-[30rem] h-[30rem] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border border-gray-300 animate-fade-in">
          <div className="px-5 py-4 bg-white border-b">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-lg font-semibold">
                  Hi, I’m <span className="text-purple-700 font-bold">Flow AI</span> – your expert operations assistant!
                </div>
                <div className="text-xs text-gray-500 mt-1">
 I’m here to streamline your organizational management tasks. Get instant guidance on HR, Finance, Transportation, Project Management, and all administrative functions within the Office Flow Admin Panel. Just ask!
                </div>
              </div>
              <button onClick={toggleChat} aria-label="Close">
                <FiX size={20} />
              </button>
            </div>
          </div>

          {/* Body (prompts + chat area) */}
          <div className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50">
            <div className="p-4 space-y-3">

              {/* Messages area (appears below prompt blocks as user chats) */}
              <div
                ref={listRef}
                className="mt-2 max-h-[12rem] overflow-y-auto space-y-2"
              >
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`max-w-[85%] px-4 py-2 rounded-xl text-sm break-words ${msg.from === "user"
                        ? "ms-auto bg-gray-200 text-gray-900"
                        : "me-auto bg-gray-100 text-gray-800"
                      }`}
                  >
                    <div className="prose prose-sm whitespace-pre-wrap">
                      <ReactMarkdown>{msg.text}</ReactMarkdown>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <span className="animate-pulse">🤖 Typing...</span>
                  </div>
                )}
              </div>
            </div>
          </div>


          <div className="flex flex-col w-full h-auto bg-gradient-to-b from-[#FFE6ED] to-[#DDBCFF] p-4 border-t">
            <div className="flex items-center relative">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Type your prompt here..."
                className="w-full px-3 h-[2.7rem] rounded-lg bg-white/90 border border-white focus:outline-none focus:ring-2 focus:ring-purple-300"
              />
              <button
                onClick={sendMessage}
                disabled={isLoading}
                className="absolute right-2 flex items-center justify-center  text-white btn-gradient pl-1 shadow transition-transform duration-300 hover:scale-105 p-1 disabled:opacity-50"
                aria-label="Send"
              >
                <IoMdSend color="white" size={22} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
